import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionAPI, usageAPI, billingAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import SubscriptionSidebarLayout from '../components/SubscriptionSidebarLayout';

/** Match addon.applicablePlanTypes (comma-separated) to a plan type without substring bugs. */
const addonAppliesToPlanType = (addon, planType) => {
    if (!planType) return false;
    if (!addon?.applicablePlanTypes) return true;
    return addon.applicablePlanTypes.split(',').map((s) => s.trim()).includes(planType);
};

const MySubscription = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [subscription, setSubscription] = useState(null);
    const [addons, setAddons] = useState([]);
    const [usage, setUsage] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [plans, setPlans] = useState([]);
    const [availableAddons, setAvailableAddons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [subscribing, setSubscribing] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const plansSectionRef = useRef(null);

    const scrollToPlans = () => plansSectionRef.current?.scrollIntoView({ behavior: 'smooth' });

    const getApplicablePlanType = () => {
        switch (user.role) {
            case 'STARTUP':
            case 'ENTREPRENEUR':
                return 'STARTUP';
            case 'INVESTOR':
                return 'INVESTOR';
            case 'INVESTOR_NETWORK':
            case 'MANAGEMENT':
                return 'INVESTOR_NETWORK';
            default:
                return null;
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [
                subscriptionData,
                addonsData,
                usageData,
                alertsData,
                plansData,
                addonsCatalogData
            ] = await Promise.all([
                subscriptionAPI.getMySubscription().catch(() => null),
                subscriptionAPI.getMyAddons().catch(() => []),
                usageAPI.getUsageSummary().catch(() => null),
                usageAPI.getUsageAlerts().catch(() => []),
                subscriptionAPI.getPlans().catch(() => []),
                subscriptionAPI.getAddons().catch(() => [])
            ]);
            setSubscription(subscriptionData);
            setAddons(addonsData);
            setUsage(usageData);
            setAlerts(alertsData);

            const applicablePlanType = getApplicablePlanType();
            let filteredPlans = applicablePlanType
                ? (plansData || []).filter(plan => plan.planType === applicablePlanType)
                : (plansData || []);
            if (user.role === 'INVESTOR') {
                filteredPlans = filteredPlans.filter(plan => plan.basePrice === 0);
            }
            filteredPlans = filteredPlans.filter((plan) => {
                if (plan.planType === 'STARTUP') {
                    const name = (plan.planName || '').toLowerCase();
                    return !name.includes('startup pro') && !name.includes('startup enterprise');
                }
                if (plan.planType === 'INVESTOR_NETWORK') {
                    const name = (plan.planName || '').toLowerCase();
                    return !name.includes('network pro') && !name.includes('network enterprise');
                }
                return true;
            });
            setPlans(filteredPlans);

            const filteredAddonsCatalog = !applicablePlanType
                ? []
                : (addonsCatalogData || []).filter((addon) => addonAppliesToPlanType(addon, applicablePlanType));
            setAvailableAddons(filteredAddonsCatalog);
        } catch (error) {
            console.error('Failed to load data:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = (immediate) => {
        setConfirm({
            isOpen: true,
            title: immediate ? 'Cancel Subscription Immediately' : 'Cancel Subscription',
            message: immediate
                ? 'Are you sure you want to cancel your subscription immediately? You will lose access to all features right away.'
                : 'Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.',
            type: 'danger',
            onConfirm: () => performCancel(immediate)
        });
    };

    const performCancel = async (immediate) => {
        setCancelling(true);
        try {
            await subscriptionAPI.cancelSubscription(immediate);
            setAlert({
                isOpen: true,
                title: 'Success',
                message: immediate ? 'Subscription cancelled immediately' : 'Subscription will be cancelled at the end of the billing period',
                type: 'success'
            });
            loadData();
        } catch (error) {
            console.error('Cancellation failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message || 'Cancellation failed', type: 'error' });
        } finally {
            setCancelling(false);
        }
    };

    const getPlanFeatures = (plan) => {
        const features = [];
        if (plan.storageLimitGb > 0) features.push(`${plan.storageLimitGb} GB Storage`);
        if (plan.investorLinkLimit > 0) features.push(`Up to ${plan.investorLinkLimit} Investor Links`);
        if (plan.planType === 'STARTUP') {
            features.push('Upload & share documents', 'Investor data room access', 'Investment tracking');
            if (plan.storageLimitGb >= 25) features.push('Priority support');
            if (plan.storageLimitGb >= 100) features.push('Dedicated account manager', 'Custom branding');
        } else if (plan.planType === 'INVESTOR') {
            features.push('View shared documents', 'Portfolio tracking', 'Investment analytics');
            if (plan.basePrice > 0) features.push('Priority deal access', 'Advanced reporting');
        } else if (plan.planType === 'INVESTOR_NETWORK') {
            features.push('Manage investor users', 'Network investment tracking', 'Commitment management');
            if (plan.investorLinkLimit >= 100) features.push('Priority support');
            if (plan.investorLinkLimit >= 500) features.push('Dedicated account manager', 'API access');
        }
        return features;
    };

    const getAddonLinesForPlan = (plan) => {
        if (!plan?.planType) return [];
        return availableAddons
            .filter((addon) => addonAppliesToPlanType(addon, plan.planType))
            .map((addon) => {
                if (addon.addonCategory === 'STORAGE') {
                    return `Storage add-on: +${addon.unitValue} GB @ ₹${addon.unitPrice}`;
                }
                if (addon.addonCategory === 'INVESTOR_LINK') {
                    return `Investor link add-on: +${addon.unitValue} investors @ ₹${addon.unitPrice}`;
                }
                return `${addon.addonName}: ₹${addon.unitPrice}`;
            });
    };

    const getRecommendedPlan = () => {
        if (!user.role) return null;
        if (user.role === 'STARTUP' || user.role === 'ENTREPRENEUR') return plans.find(p => p.planCode === 'STARTUP_BASE');
        if (user.role === 'INVESTOR') return plans.find(p => p.planCode === 'INVESTOR_FREE');
        if (user.role === 'INVESTOR_NETWORK' || user.role === 'MANAGEMENT') return plans.find(p => p.planCode === 'NETWORK_BASE');
        return null;
    };

    const isPlanActive = (planCode) =>
        subscription?.plan?.planCode === planCode && subscription?.status === 'ACTIVE';

    const hasActiveSubscription = () => subscription?.status === 'ACTIVE';

    const isUpgrade = (plan) => {
        if (!hasActiveSubscription()) return false;
        return (plan.basePrice || 0) > (subscription?.plan?.basePrice || 0);
    };

    const handleSubscribe = (plan) => {
        const rolePlanMap = { STARTUP: 'STARTUP', ENTREPRENEUR: 'STARTUP', INVESTOR: 'INVESTOR', INVESTOR_NETWORK: 'INVESTOR_NETWORK', MANAGEMENT: 'INVESTOR_NETWORK' };
        const expectedPlanType = rolePlanMap[user.role];
        if (plan.planType !== expectedPlanType) {
            setAlert({ isOpen: true, title: 'Invalid Plan', message: `This plan is not available for your account type.`, type: 'error' });
            return;
        }
        const hasExisting = hasActiveSubscription();
        const currentPlanName = subscription?.plan?.planName || '';
        const isAnnual = plan.billingCycle === 'ANNUAL';
        const priceText = plan.basePrice === 0 ? 'Free' : (isAnnual ? `₹${plan.basePrice}/year` : `₹${plan.basePrice}/month`);
        let title, message;
        if (hasExisting) {
            const upgradeOrSwitch = isUpgrade(plan) ? 'Upgrade' : 'Switch';
            title = `${upgradeOrSwitch} to ${plan.planName}`;
            message = `You are currently on ${currentPlanName}. ${upgradeOrSwitch}ing to ${plan.planName} (${priceText}) will cancel your current subscription and start the new plan immediately. Continue?`;
        } else {
            title = 'Subscribe to Plan';
            message = plan.basePrice === 0 ? `Start your free ${plan.planName} subscription?` : `Subscribe to ${plan.planName} for ${priceText}?`;
        }
        setConfirm({
            isOpen: true,
            title,
            message,
            type: hasExisting ? 'warning' : 'info',
            onConfirm: () => performSubscribe(plan.planCode, hasExisting)
        });
    };

    const performSubscribe = async (planCode, cancelExisting = false) => {
        setSubscribing(true);
        try {
            if (cancelExisting) await subscriptionAPI.cancelSubscription(true);
            await subscriptionAPI.subscribe(planCode);
            setAlert({ isOpen: true, title: 'Success', message: cancelExisting ? 'Subscription upgraded successfully!' : 'Subscription created successfully!', type: 'success' });
            loadData();
        } catch (error) {
            console.error('Subscription failed:', error);
            setAlert({ isOpen: true, title: 'Error', message: error.message || 'Subscription failed', type: 'error' });
        } finally {
            setSubscribing(false);
        }
    };

    const getUsageColor = (percentage) => {
        if (percentage >= 100) return 'red';
        if (percentage >= 90) return 'orange';
        if (percentage >= 80) return 'yellow';
        return 'green';
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    };

    if (loading) {
        return (
            <SubscriptionSidebarLayout activeItem="subscription">
                <div className="flex items-center justify-center min-h-[40vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
                </div>
            </SubscriptionSidebarLayout>
        );
    }

    return (
        <SubscriptionSidebarLayout activeItem="subscription">
            {/* Header */}
            <div className="mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">My Subscription</h1>
                    <p className="text-gray-600 mt-1">Manage your subscription, usage, and change plan below</p>
                </div>
            </div>

            {/* Usage Alerts */}
            {alerts.length > 0 && (
                <div className="mb-8 space-y-4">
                    {alerts.map((alertItem) => (
                        <div
                            key={alertItem.id}
                            className={`p-4 rounded-lg border ${
                                alertItem.alertLevel === 'EXCEEDED'
                                    ? 'bg-red-50 border-red-300'
                                    : alertItem.alertLevel === 'CRITICAL'
                                        ? 'bg-orange-50 border-orange-300'
                                        : 'bg-yellow-50 border-yellow-300'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <svg className={`w-6 h-6 mr-3 ${
                                        alertItem.alertLevel === 'EXCEEDED' ? 'text-red-600' :
                                        alertItem.alertLevel === 'CRITICAL' ? 'text-orange-600' :
                                        'text-yellow-600'
                                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <div>
                                        <h4 className={`font-semibold ${
                                            alertItem.alertLevel === 'EXCEEDED' ? 'text-red-800' :
                                            alertItem.alertLevel === 'CRITICAL' ? 'text-orange-800' :
                                            'text-yellow-800'
                                        }`}>
                                            {alertItem.usageType === 'STORAGE' ? 'Storage' : 'Investor Links'} {alertItem.alertLevel}
                                        </h4>
                                        <p className={`text-sm ${
                                            alertItem.alertLevel === 'EXCEEDED' ? 'text-red-600' :
                                            alertItem.alertLevel === 'CRITICAL' ? 'text-orange-600' :
                                            'text-yellow-600'
                                        }`}>
                                            {alertItem.message} - {alertItem.displayUsed} / {alertItem.displayLimit}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={scrollToPlans}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                                >
                                    Upgrade Storage
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Subscription Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Current Plan */}
                    {subscription && subscription.id ? (
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Current Plan</h2>
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    subscription.status === 'ACTIVE'
                                        ? 'bg-green-100 text-green-800'
                                        : subscription.status === 'CANCELLED'
                                            ? 'bg-gray-100 text-gray-800'
                                            : 'bg-red-100 text-red-800'
                                }`}>
                                    {subscription.status}
                                </span>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Plan</span>
                                    <span className="font-semibold">{subscription.plan?.planName}</span>
                                </div>
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Price</span>
                                    <span className="font-semibold">
                                        {subscription.plan?.basePrice === 0
                                            ? 'Free'
                                            : subscription.plan?.billingCycle === 'ANNUAL'
                                                ? `₹${subscription.plan?.basePrice}/year`
                                                : `₹${subscription.plan?.basePrice}/month`}
                                    </span>
                                </div>
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Storage</span>
                                    <span className="font-semibold">{subscription.plan?.storageLimitGb} GB</span>
                                </div>
                                {subscription.plan?.investorLinkLimit > 0 && (
                                    <div className="flex justify-between py-3 border-b">
                                        <span className="text-gray-600">Investor Links</span>
                                        <span className="font-semibold">{subscription.plan?.investorLinkLimit}</span>
                                    </div>
                                )}
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Started On</span>
                                    <span className="font-semibold">
                                        {new Date(subscription.startDate).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="flex justify-between py-3 border-b">
                                    <span className="text-gray-600">Renews On</span>
                                    <span className="font-semibold">
                                        {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between py-3">
                                    <span className="text-gray-600">Auto-Renew</span>
                                    <span className={`font-semibold ${subscription.autoRenew ? 'text-green-600' : 'text-red-600'}`}>
                                        {subscription.autoRenew ? 'Enabled' : 'Disabled'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex space-x-4 mt-6">
                                {subscription.status === 'ACTIVE' ? (
                                    <>
                                        <button
                                            onClick={() => handleCancel(false)}
                                            disabled={cancelling}
                                            className="flex-1 py-2 px-4 border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
                                        >
                                            Cancel at Period End
                                        </button>
                                        <button
                                            onClick={() => handleCancel(true)}
                                            disabled={cancelling}
                                            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                        >
                                            Cancel Immediately
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={scrollToPlans}
                                        className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Choose a Plan
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">No Active Subscription</h2>
                            <p className="text-gray-600 mb-6">You don't have an active subscription yet. Choose a plan below.</p>
                            <button
                                onClick={scrollToPlans}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                View Plans Below
                            </button>
                        </div>
                    )}

                    {/* Purchased Addons */}
                    {addons.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Purchased Add-ons</h2>
                            <div className="space-y-4">
                                {addons.map((addon) => (
                                    <div key={addon.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
                                        <div>
                                            <h3 className="font-semibold">{addon.addon?.addonName}</h3>
                                            <p className="text-sm text-gray-500">
                                                Quantity: {addon.quantity} | Purchased: {new Date(addon.purchaseDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold">{addon.totalAmount}</div>
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                addon.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {addon.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Usage Summary */}
                <div className="space-y-6">
                    {usage && (
                        <>
                            {/* Storage Usage */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Storage Usage</h3>
                                {usage.storageUsage && (
                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-600">Used</span>
                                            <span className="font-semibold">
                                                {usage.storageUsage.displayUsed} / {usage.storageUsage.displayLimit}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                                            <div
                                                className="h-full transition-all"
                                                style={{
                                                    width: `${Math.min(usage.storageUsage.percentage, 100)}%`,
                                                    backgroundColor: getUsageColor(usage.storageUsage.percentage) === 'green' ? '#10b981' :
                                                                   getUsageColor(usage.storageUsage.percentage) === 'yellow' ? '#f59e0b' :
                                                                   getUsageColor(usage.storageUsage.percentage) === 'orange' ? '#f97316' :
                                                                   '#ef4444'
                                                }}
                                            />
                                        </div>
                                        <div className="text-right text-sm mt-1 text-gray-500">
                                            {usage.storageUsage.percentage.toFixed(1)}%
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Investor Link Usage */}
                            {usage.investorLinkUsage && (
                                <div className="bg-white rounded-lg shadow p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Investor Links</h3>
                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-600">Used</span>
                                            <span className="font-semibold">
                                                {usage.investorLinkUsage.used} / {usage.investorLinkUsage.limit}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                                            <div
                                                className="h-full transition-all"
                                                style={{
                                                    width: `${Math.min(usage.investorLinkUsage.percentage, 100)}%`,
                                                    backgroundColor: getUsageColor(usage.investorLinkUsage.percentage) === 'green' ? '#10b981' :
                                                                   getUsageColor(usage.investorLinkUsage.percentage) === 'yellow' ? '#f59e0b' :
                                                                   getUsageColor(usage.investorLinkUsage.percentage) === 'orange' ? '#f97316' :
                                                                   '#ef4444'
                                                }}
                                            />
                                        </div>
                                        <div className="text-right text-sm mt-1 text-gray-500">
                                            {usage.investorLinkUsage.percentage.toFixed(1)}%
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                </div>
            </div>

            {/* Available Plans - same as subscription/plans, in-page */}
            <div ref={plansSectionRef} className="mt-12">
                {user.role === 'INVESTOR' && (
                    <div className="mb-8 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Add documents or startups?</h2>
                        <p className="text-gray-600 mb-4">
                            Pay a one-time fee of <strong>₹1,000</strong> to upload documents or to be linked to startups.
                        </p>
                        <button
                            onClick={() => navigate(`/${tenantId}/investor-activity-fee`)}
                            className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700"
                        >
                            Pay ₹1,000 (one-time)
                        </button>
                    </div>
                )}
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Available Plans</h2>
                <p className="text-gray-600 mb-6">Choose or change your plan below</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {plans.map((plan) => {
                        const isRecommended = getRecommendedPlan()?.planCode === plan.planCode;
                        const isActive = isPlanActive(plan.planCode);
                        const features = [...getPlanFeatures(plan), ...getAddonLinesForPlan(plan)];
                        return (
                            <div
                                key={plan.id}
                                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${isRecommended ? 'ring-4 ring-blue-500 transform scale-105' : ''} ${isActive ? 'ring-4 ring-green-500' : ''}`}
                            >
                                {isRecommended && (
                                    <div className="absolute top-0 right-0 bg-blue-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">Recommended</div>
                                )}
                                {isActive && (
                                    <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">Current Plan</div>
                                )}
                                <div className="p-8">
                                    <div className="text-center mb-6">
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.planName}</h3>
                                        <div className="text-4xl font-bold text-blue-600">
                                            {plan.basePrice === 0 ? 'Free' : `₹${plan.basePrice}`}
                                        </div>
                                        <p className="text-gray-500 mt-1">
                                            {plan.basePrice === 0 ? 'on signup' : (plan.billingCycle === 'ANNUAL' ? 'per year' : 'per month')}
                                        </p>
                                    </div>
                                    <ul className="space-y-4 mb-8">
                                        {features.map((feature, index) => (
                                            <li key={index} className="flex items-start">
                                                <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={isActive || subscribing}
                                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${isActive
                                            ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                            : hasActiveSubscription() && !isActive
                                                ? isUpgrade(plan)
                                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                    : 'bg-orange-600 text-white hover:bg-orange-700'
                                                : isRecommended
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                    : 'bg-gray-600 text-white hover:bg-gray-700'
                                            }`}
                                    >
                                        {isActive ? 'Current Plan' : hasActiveSubscription() ? (isUpgrade(plan) ? 'Upgrade Plan' : 'Switch Plan') : 'Subscribe Now'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {availableAddons.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Add-ons</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {availableAddons.map((addon) => (
                                <div key={addon.id} className="bg-white rounded-lg shadow p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{addon.addonName}</h3>
                                    <p className="text-gray-600 text-sm mb-4">
                                        {addon.addonCategory === 'STORAGE' ? `Add ${addon.unitValue} GB of storage` : `Add ${addon.unitValue} investor links`}
                                    </p>
                                    <div className="text-2xl font-bold text-blue-600 mb-4">₹{addon.unitPrice}</div>
                                    <button
                                        onClick={() => navigate(`/${tenantId}/purchase-addon`, { state: { addon } })}
                                        className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        Purchase
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => {})}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
        </SubscriptionSidebarLayout>
    );
};

export default MySubscription;
